import type { ScheduleEvent } from '@/services/scheduleApi'

/*
 * Даты графика — местные, `YYYY-MM-DD` и `HH:mm` (см. `sc_atyrau_backend/docs/schedule-api.md`).
 * `toISOString()` здесь брать нельзя: он даёт дату по UTC, и событие, созданное
 * в 02:00 по Атырау, легло бы на вчерашний день.
 */
export function toIsoDate(date: Date) {
  const year = date.getFullYear()
  const month = String(date.getMonth() + 1).padStart(2, '0')
  const day = String(date.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

/** `YYYY-MM-DD` → местный полдень: полночь сдвигается в соседние сутки при переводе часов. */
export function fromIsoDate(iso: string) {
  const [y, m, d] = iso.split('-').map(Number)
  return new Date(y, m - 1, d, 12, 0, 0, 0)
}

export function isSameDay(a: Date, b: Date) {
  return toIsoDate(a) === toIsoDate(b)
}

export function addDays(date: Date, days: number) {
  const next = new Date(date)
  next.setDate(next.getDate() + days)
  return next
}

function startOfWeek(date: Date) {
  const copy = new Date(date)
  const day = copy.getDay()
  const diff = day === 0 ? -6 : 1 - day
  copy.setDate(copy.getDate() + diff)
  copy.setHours(12, 0, 0, 0)
  return copy
}

export function getWeekDays(reference = new Date()): Date[] {
  const start = startOfWeek(reference)
  return Array.from({ length: 7 }, (_, index) => {
    const day = new Date(start)
    day.setDate(start.getDate() + index)
    return day
  })
}

export function timeToMinutes(time: string) {
  const [hours, minutes] = time.split(':').map(Number)
  return hours * 60 + minutes
}

export function nowMinutes(now = new Date()) {
  return now.getHours() * 60 + now.getMinutes()
}

export function eventsLabel(count: number) {
  const mod10 = count % 10
  const mod100 = count % 100
  if (mod10 === 1 && mod100 !== 11) return 'событие'
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 12 || mod100 > 14)) return 'события'
  return 'событий'
}

export function formatScheduleDayLong(date: Date) {
  const formatted = new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
  }).format(date)
  const weekday = new Intl.DateTimeFormat('ru-RU', { weekday: 'long' }).format(date)
  return `${formatted}, ${weekday}`
}

export function formatScheduleDayShort(date: Date) {
  return new Intl.DateTimeFormat('ru-RU', {
    day: 'numeric',
    month: 'short',
  }).format(date)
}

export function formatWeekdayShort(date: Date) {
  return new Intl.DateTimeFormat('ru-RU', { weekday: 'short' }).format(date)
}

/**
 * Ближайшее событие дня: идущее сейчас или следующее. Только для сегодняшнего
 * дня — у другого дня «ближайшего» нет, и показывать первое событие вчера как
 * «ближайшее» было бы неправдой.
 */
export function pickUpcomingEvent(events: ScheduleEvent[], now = new Date()) {
  const current = nowMinutes(now)
  return events.find((event) => {
    const end = event.endTime ? timeToMinutes(event.endTime) : timeToMinutes(event.startTime) + 30
    return end > current
  })
}

export function isEventPast(event: ScheduleEvent, now = new Date()) {
  const end = event.endTime ? timeToMinutes(event.endTime) : timeToMinutes(event.startTime) + 30
  return end <= nowMinutes(now)
}
